import type {
  Keypair,
  PublicKey,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import type { OracleJob } from "@switchboard-xyz/common";

import {
  buildDepositTx,
  buildRelayPayload,
  decryptData,
  encryptData,
  fetchCommitments,
  fetchDepositCountByCommitment,
  fetchLatestTransactions,
  fetchPlatformFee,
  fetchTxCounts,
  fetchTxStatus,
  fetchWalletBalance,
  getCommitmentPDA,
  getEncryptionKey as deriveEncryptionKey,
  getNetworkStatePDA,
  getNullifierPDA,
  getPoolPDA,
  getProgramId,
  getTokenPoolPDA,
  getTreasuryPDA,
  normalizePubkeyString,
  pollRelayerJob,
  submitToRelayer,
  validateNullifier,
  type BuildDepositTxParams,
  type BuildRelayPayloadParams,
  type EncryptKeyWallet,
  type FetchWalletBalanceOptions,
  type RelayPayload,
} from "./arcaneProgram";
import {
  amountToFieldElement,
  convertProofToBytes,
  createCommitment,
  createNullifierHash,
  formatNoteForDisplay,
  generateDepositParams,
  generateMerkleProof,
  generateRandomFieldElement,
  generateWithdrawProof,
  type GenerateWithdrawProofOptions,
  type GenerateDepositParamsResult,
  parseSecretNote,
  publicKeyToFieldElement,
  to32ByteBuffer,
  toFieldElementHex,
  toFixedHex,
} from "./zkProof";
import {
  createRuntimeConfig,
  popRuntimeContext,
  pushRuntimeContext,
  type ArcaneSdkInitOptions,
  type ArcaneSdkRuntimeConfig,
  type Relayer,
} from "./config/runtime";
import {
  getArcaneTokenMintForNetwork,
  getDepositAmounts,
  getIndexerUrlForNetwork,
  getProgramIdForNetwork,
  getRiskApiUrlForNetwork,
  getTokenDecimals,
  getTokenMintsForNetwork,
  type DepositTokenSymbol,
  type Network,
  type TokenDecimalsSymbol,
  type TokenSymbol,
} from "./config/constants";
import { fetchRelayersForNetwork } from "./config/relayers";
import { getOracleQuote, getRangeRiskScoreJob } from "./rangeSDK";
import { appendIndexerAsset, type IndexerAsset } from "./tokenAssets";
import {
  WITHDRAW_CIRCUIT_WASM,
  WITHDRAW_CIRCUIT_ZKEY,
  resolveCircuitPathsFromBase,
  type CircuitPaths,
} from "./circuits/constants";
import { loadDefaultCircuitPaths } from "./circuits/loadDefaultCircuitPaths.js";
import {
  executeWithdraw,
  type WithdrawParams,
  type WithdrawResult,
} from "./withdraw/execute";
import {
  getQuote,
  type GetQuoteParams,
  type GetQuoteResponse,
} from "./quote";
import {
  resolveNoteAsset,
  type ResolvedNoteAsset,
} from "./withdraw/assets";
import {
  assertWithdrawRecipientAllowed as assertRecipientAllowed,
  MALICIOUS_RECIPIENT_ERROR,
} from "./withdraw/risk";
import {
  executeArcaneTransfer,
  type ArcaneTransferDepositFailure,
  type ArcaneTransferItem,
  type ArcaneTransferOptions,
  type ArcaneTransferResult,
  type ArcaneTransferWithdrawFailure,
} from "./transfer/arcaneTransfer";

/**
 * Arcane SDK client. Create one instance per configuration (network, RPC, API keys).
 *
 * @example
 * ```js
 * const { ArcaneSDK } = require('arcane-privacy-sdk');
 * const sdk = await ArcaneSDK.create({
 *   rangeApiKey: process.env.RANGE_API_KEY,
 *   rpcEndpoint: 'https://api.devnet.solana.com',
 *   wallet,
 * });
 * console.log(sdk.config.relayers);
 * ```
 *
 * @example React (wallet-adapter)
 * ```tsx
 * import { useArcaneSdk } from 'arcane-privacy-sdk/react';
 *
 * function MyComponent() {
 *   const sdk = useArcaneSdk({ rangeApiKey: import.meta.env.VITE_RANGE_API_KEY });
 *   if (!sdk) return null;
 *   // ...
 * }
 * ```
 *
 * @example
 * ```ts
 * import { ArcaneSDK } from 'arcane-privacy-sdk';
 * const sdk = await ArcaneSDK.create({ rangeApiKey, rpcEndpoint, wallet });
 * await sdk.getEncryptionKey(wallet);
 * const { note } = sdk.generateDepositParams(1_000_000_000n, null);
 * await sdk.buildDepositTx({ senderPublicKey: wallet.publicKey, note, onChainBackup: true });
 * ```
 */
export class ArcaneSDK {
  static readonly VERSION = "0.0.1";

  readonly config: ArcaneSdkRuntimeConfig;

  private constructor(config: ArcaneSdkRuntimeConfig) {
    this.config = config;
  }

  /** Create an SDK instance (loads relayers for the inferred network). */
  static async create(options: ArcaneSdkInitOptions): Promise<ArcaneSDK> {
    const config = await createRuntimeConfig(options);
    return new ArcaneSDK(config);
  }

  private run<T>(fn: () => T): T {
    pushRuntimeContext(this.config);
    try {
      return fn();
    } finally {
      popRuntimeContext();
    }
  }

  private async runAsync<T>(fn: () => Promise<T>): Promise<T> {
    pushRuntimeContext(this.config);
    try {
      return await fn();
    } finally {
      popRuntimeContext();
    }
  }

  // ── Config ───────────────────────────────────────────────────────────

  getConfig(): ArcaneSdkRuntimeConfig {
    return this.config;
  }

  getRangeApiKey(): string {
    return this.config.rangeApiKey;
  }

  getRpcEndpoint(): string {
    return this.config.rpcEndpoint;
  }

  getNetwork(): Network {
    return this.config.network;
  }

  getProgramIdString(): string {
    return this.config.programId;
  }

  /** Encryption key derived by {@link getEncryptionKey}; undefined until set. */
  get encryptKey(): Uint8Array | undefined {
    return this.config.encryptKey;
  }

  /** Relayers loaded at initialization; use {@link fetchRelayers} to refresh. */
  get relayers(): Relayer[] {
    return this.config.relayers;
  }

  getIndexerUrl(): string {
    return getIndexerUrlForNetwork(this.config.network);
  }

  getRiskApiUrl(): string {
    return getRiskApiUrlForNetwork(this.config.network);
  }

  getTokenMints() {
    return getTokenMintsForNetwork(this.config.network);
  }

  getArcaneTokenMint(): string {
    return getArcaneTokenMintForNetwork(this.config.network);
  }

  getTokenMint(symbol: TokenSymbol): string {
    return getTokenMintsForNetwork(this.config.network)[symbol];
  }

  getDepositAmounts(symbol: DepositTokenSymbol) {
    return getDepositAmounts(symbol);
  }

  getTokenDecimals(symbol: TokenDecimalsSymbol): number {
    return getTokenDecimals(symbol);
  }

  async resolveCircuitPaths(base?: string): Promise<CircuitPaths> {
    if (base !== undefined) {
      return resolveCircuitPathsFromBase(base);
    }

    return loadDefaultCircuitPaths();
  }

  // ── Range / Oracle ───────────────────────────────────────────────────

  getRangeRiskScoreJob(address: string): OracleJob {
    return getRangeRiskScoreJob(address);
  }

  getOracleQuote(
    payer: Keypair | PublicKey,
    addressToCheck: string
  ): Promise<{
    queueAccount: PublicKey;
    sigVerifyIx: TransactionInstruction;
  }> {
    return this.runAsync(() => getOracleQuote(payer, addressToCheck));
  }

  // ── On-chain program ─────────────────────────────────────────────────

  getProgramId(): PublicKey {
    return this.run(() => getProgramId());
  }

  getPoolPDA(denominationLamports: number | bigint) {
    return this.run(() => getPoolPDA(denominationLamports));
  }

  getTokenPoolPDA(
    mint: PublicKey | string,
    denominationSmallestUnits: number | bigint
  ) {
    return this.run(() => getTokenPoolPDA(mint, denominationSmallestUnits));
  }

  getTreasuryPDA() {
    return this.run(() => getTreasuryPDA());
  }

  getCommitmentPDA(commitmentBigInt: bigint | number | string) {
    return this.run(() => getCommitmentPDA(commitmentBigInt));
  }

  getNullifierPDA(nullifierHashBigInt: bigint | number | string) {
    return this.run(() => getNullifierPDA(nullifierHashBigInt));
  }

  getNetworkStatePDA() {
    return this.run(() => getNetworkStatePDA());
  }

  buildDepositTx(params: BuildDepositTxParams): Promise<{
    tx: Transaction;
    note: string;
  }> {
    return this.runAsync(() => buildDepositTx(params));
  }

  /**
   * Withdraw to `recipient` using a secret `note`.
   * Relayer is chosen at random from {@link config.relayers}, or by {@link WithdrawParams.relayerIdx}.
   */
  withdraw(params: WithdrawParams, relayerIdx: number = -1): Promise<WithdrawResult> {
    return this.runAsync(() => executeWithdraw(params, relayerIdx));
  }

  /**
   * Private transfer: deposit into the pool (split by supported denominations),
   * then withdraw each successful note to the recipient.
   *
   * Requires {@link getEncryptionKey} before calling (deposits use on-chain backup).
   */
  arcaneTransfer(
    items: ArcaneTransferItem[],
  ): Promise<ArcaneTransferResult> {
    return this.runAsync(() => executeArcaneTransfer(items));
  }

  /** Validate a withdraw recipient against the risk API. */
  assertWithdrawRecipientAllowed(address: string): Promise<void> {
    return assertRecipientAllowed(
      address,
      getRiskApiUrlForNetwork(this.config.network)
    );
  }

  /** Resolve the token asset encoded in a secret note. */
  resolveNoteAsset(
    amount: string,
    mint?: string | null,
    tokenNameHint?: TokenDecimalsSymbol
  ): ResolvedNoteAsset {
    return resolveNoteAsset(
      this.config.network,
      amount,
      mint ?? null,
      tokenNameHint ?? null
    );
  }

  fetchPlatformFee(): Promise<string> {
    return this.runAsync(() => fetchPlatformFee());
  }

  /** Fetch a withdraw quote from the indexer. */
  getQuote(params: GetQuoteParams): Promise<GetQuoteResponse> {
    return this.runAsync(() => getQuote(params));
  }

  // ── Indexer ──────────────────────────────────────────────────────────

  fetchCommitments(
    denomination: number | bigint | string,
    indexerAsset?: IndexerAsset
  ) {
    return this.runAsync(() =>
      fetchCommitments(denomination, indexerAsset)
    );
  }

  validateNullifier(nullifierHash: string, indexerAsset?: IndexerAsset) {
    return this.runAsync(() =>
      validateNullifier(nullifierHash, indexerAsset)
    );
  }

  fetchTxStatus(
    commitmentHex: string,
    nullifierHashHex: string,
    indexerAsset?: IndexerAsset
  ) {
    return this.runAsync(() =>
      fetchTxStatus(commitmentHex, nullifierHashHex, indexerAsset)
    );
  }

  fetchDepositCountByCommitment(
    commitment: string,
    denomination: number | bigint | string,
    indexerAsset?: IndexerAsset
  ) {
    return this.runAsync(() =>
      fetchDepositCountByCommitment(commitment, denomination, indexerAsset)
    );
  }

  fetchWalletBalance(wallet: string, options?: FetchWalletBalanceOptions) {
    return this.runAsync(() => fetchWalletBalance(wallet, options));
  }

  /** Re-fetch relayers from the indexer and update {@link config.relayers}. */
  fetchRelayers(): Promise<Relayer[]> {
    return this.runAsync(async () => {
      const relayers = await fetchRelayersForNetwork(
        this.config.network,
        getIndexerUrlForNetwork(this.config.network)
      );
      this.config.relayers = relayers;
      return relayers;
    });
  }

  fetchLatestTransactions(type = "Deposit", indexerAsset?: IndexerAsset) {
    return this.runAsync(() =>
      fetchLatestTransactions(type, indexerAsset)
    );
  }

  fetchTxCounts(type = "Deposit", indexerAsset?: IndexerAsset) {
    return this.runAsync(() => fetchTxCounts(type, indexerAsset));
  }

  // ── Relayer ──────────────────────────────────────────────────────────

  normalizePubkeyString(value: unknown): string {
    return normalizePubkeyString(value);
  }

  buildRelayPayload(params: BuildRelayPayloadParams): RelayPayload {
    return buildRelayPayload(params);
  }

  submitToRelayer(relayerUrl: string, payload: RelayPayload) {
    return submitToRelayer(relayerUrl, payload);
  }

  pollRelayerJob(relayerUrl: string, jobId: string, maxWaitMs?: number) {
    return pollRelayerJob(relayerUrl, jobId, maxWaitMs);
  }

  // ── Encryption ───────────────────────────────────────────────────────

  /**
   * Derives the note encryption key from a wallet signature and stores it on
   * {@link config.encryptKey}. Required before {@link buildDepositTx} with
   * `onChainBackup: true`.
   *
   * Accepts a frontend wallet adapter (`signMessage`) or a backend {@link Keypair}.
   */
  async getEncryptionKey(wallet: EncryptKeyWallet): Promise<Uint8Array> {
    const key = await deriveEncryptionKey(wallet);
    this.config.encryptKey = key;
    return key;
  }

  encryptData(key: Uint8Array, plaintext: string) {
    return encryptData(key, plaintext);
  }

  decryptData(key: Uint8Array, combined: Uint8Array) {
    return decryptData(key, combined);
  }

  // ── ZK proofs ────────────────────────────────────────────────────────

  generateRandomFieldElement() {
    return generateRandomFieldElement();
  }

  createCommitment(
    nullifier: bigint | number | string,
    secret: bigint | number | string
  ) {
    return createCommitment(nullifier, secret);
  }

  createNullifierHash(nullifier: bigint | number | string) {
    return createNullifierHash(nullifier);
  }

  generateDepositParams(
    amountLamports: bigint | number | string,
    mint?: string | null
  ): GenerateDepositParamsResult {
    return generateDepositParams(amountLamports, mint);
  }

  parseSecretNote(note: string) {
    return parseSecretNote(note);
  }

  publicKeyToFieldElement(publicKeyBytes: Uint8Array | number[]) {
    return publicKeyToFieldElement(publicKeyBytes);
  }

  amountToFieldElement(amount: bigint | number | string) {
    return amountToFieldElement(amount);
  }

  generateMerkleProof(
    depositParams: Record<string, unknown>,
    leaves: unknown[]
  ) {
    return generateMerkleProof(depositParams, leaves);
  }

  generateWithdrawProof(
    depositParams: Record<string, unknown>,
    leaves: unknown[],
    recipient: PublicKey | string,
    relayer: PublicKey | string,
    fee: bigint | number | string,
    refund: bigint | number | string,
    options?: GenerateWithdrawProofOptions
  ) {
    return this.runAsync(() =>
      generateWithdrawProof(
        depositParams,
        leaves,
        recipient,
        relayer,
        fee,
        refund,
        options
      )
    );
  }

  convertProofToBytes(proof: unknown) {
    return convertProofToBytes(proof);
  }

  to32ByteBuffer(bigInt: bigint | number | string) {
    return to32ByteBuffer(bigInt);
  }

  toFixedHex(value: bigint | number | string, length = 32) {
    return toFixedHex(value, length);
  }

  toFieldElementHex(value: bigint | number | string) {
    return toFieldElementHex(value);
  }

  formatNoteForDisplay(note: string, segmentLen = 8) {
    return formatNoteForDisplay(note, segmentLen);
  }

  // ── Token assets ─────────────────────────────────────────────────────

  appendIndexerAsset(
    params: URLSearchParams,
    indexerAsset?: IndexerAsset
  ): void {
    appendIndexerAsset(params, indexerAsset);
  }

  /** Program ID for a cluster without an SDK instance. */
  static getProgramIdForNetwork(network: Network): string {
    return getProgramIdForNetwork(network);
  }
}

export type {
  ArcaneSdkInitOptions,
  ArcaneSdkRuntimeConfig,
  Wallet,
  Relayer,
} from "./config/runtime";
export type {
  DepositTokenSymbol,
  Network,
  TokenDecimalsSymbol,
  TokenSymbol,
} from "./config/constants";
export type { IndexerAsset } from "./tokenAssets";
export type {
  BuildDepositTxParams,
  BuildRelayPayloadParams,
  EncryptKeyWallet,
  FetchWalletBalanceOptions,
  RelayPayload,
} from "./arcaneProgram";
export type { WithdrawParams, WithdrawResult, WithdrawStatus } from "./withdraw/execute";
export type {
  ArcaneTransferDepositFailure,
  ArcaneTransferItem,
  ArcaneTransferOptions,
  ArcaneTransferResult,
  ArcaneTransferWithdrawFailure,
} from "./transfer/arcaneTransfer";
export type { ResolvedNoteAsset } from "./withdraw/assets";
export {
  getQuote,
  type GetQuoteParams,
  type GetQuoteResponse,
  type Quote,
  type QuoteRelayer,
  type QuoteSplit,
  type QuoteTransaction,
} from "./quote";
export { MALICIOUS_RECIPIENT_ERROR } from "./withdraw/risk";
